"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";
import { CalendarDays, Clock3, GraduationCap, ShieldCheck } from "lucide-react";
import { trackAddToCart, trackCourseEnroll } from "@/utils/gtm";
import WhatsAppButton from "@/components/WhatsApp/WhatsAppButton";
import { whatsappMessages } from "@/utils/whatsapp";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import { FORM_CONTEXTS } from "@/utils/innovlistWidget";

export interface EnrollCTAWidgetProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  courseData?: any;
  nextBatchStarts?: string;
}

export default function EnrollCTAWidget({
  courseData,
  nextBatchStarts = "Starting Soon",
}: EnrollCTAWidgetProps) {
  const pathname = usePathname();

  const courseTitle = useMemo(() => {
    return (
      courseData?.title ||
      courseData?.hero?.headline ||
      "Course Enrollment"
    );
  }, [courseData]);

  const courseSlug = useMemo(() => {
    if (typeof courseData?.slug === "string" && courseData.slug.length > 0) {
      return courseData.slug.replace("/courses/", "");
    }

    return (pathname || "").replace("/courses/", "");
  }, [courseData, pathname]);

  const duration = courseData?.courseDetails?.duration || "Flexible";
  const level = courseData?.courseDetails?.level || "All Levels";
  const schedule = courseData?.courseDetails?.schedule || "Weekday / Weekend";

  const rawPrice = courseData?.price;
  const priceText = rawPrice
    ? String(rawPrice).startsWith("$")
      ? String(rawPrice)
      : `$${String(rawPrice)}`
    : "Get Pricing";

  const averageRating = Number(courseData?.averageRating ?? courseData?.rating?.average ?? 4.8);
  const reviewCount = Number(courseData?.totalReviews ?? courseData?.rating?.totalReviews ?? 247);

  const renderStars = () => {
    const safeRating = Math.max(0, Math.min(5, averageRating || 0));
    const fullStars = Math.floor(safeRating);
    const hasHalfStar = safeRating - fullStars >= 0.5;

    return Array.from({ length: 5 }, (_, index) => {
      if (index < fullStars) {
        return <span key={`full-${index}`} className="text-[#f59e0b]">★</span>;
      }

      if (index === fullStars && hasHalfStar) {
        return (
          <span key={`half-${index}`} className="relative inline-block leading-none" aria-hidden="true">
            <span className="text-[#d1d5db]">★</span>
            <span className="absolute left-0 top-0 w-1/2 overflow-hidden text-[#f59e0b]">★</span>
          </span>
        );
      }

      return <span key={`empty-${index}`} className="text-[#d1d5db]">★</span>;
    });
  };

  const handleEnroll = () => {
    trackCourseEnroll(courseTitle, courseSlug);
    trackAddToCart({
      slug: `/courses/${courseSlug}`,
      title: courseTitle,
      category: "Courses",
      price: 0,
    });
  };

  return (
    <>
      <aside className="hidden lg:block w-full lg:w-[360px]">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6 sticky top-24">
          <p className="text-xs uppercase tracking-wide text-[#1B61FF] font-bold mb-2">Enroll Today</p>
          <h3 className="text-[#1A1A1A] text-xl font-extrabold mb-5 leading-tight">{courseTitle}</h3>

          <div className="space-y-4 mb-6 text-sm">
            <div className="flex items-center justify-between gap-3 text-[#1A1A1A]">
              <span className="inline-flex items-center gap-2 font-medium">
                <Clock3 size={16} className="text-[#1B61FF]" />
                Duration
              </span>
              <span className="font-bold text-right">{duration}</span>
            </div>
            <div className="flex items-center justify-between gap-3 text-[#1A1A1A]">
              <span className="inline-flex items-center gap-2 font-medium">
                <GraduationCap size={16} className="text-[#1B61FF]" />
                Level
              </span>
              <span className="font-bold">{level}</span>
            </div>
            <div className="flex items-center justify-between gap-3 text-[#1A1A1A]">
              <span className="inline-flex items-center gap-2 font-medium">
                <CalendarDays size={16} className="text-[#1B61FF]" />
                Schedule
              </span>
              <span className="font-bold text-right">{schedule}</span>
            </div>
          </div>

          <div className="hidden rounded-xl bg-[#F0F4FF] px-4 py-3 mb-5">
            <p className="text-xs uppercase tracking-wide text-[#666666] font-semibold">Pricing</p>
            <p className="text-[#1A1A1A] text-2xl font-extrabold mt-1">{priceText}</p>
          </div>

          <p className="text-sm text-[#666666] mb-5">
            Next batch starts: <span className="font-semibold text-[#1A1A1A]">{nextBatchStarts}</span>
          </p>

          <div className="flex items-center gap-2 text-sm mb-6">
            <span className="inline-flex items-center gap-0.5" aria-label={`Rated ${averageRating.toFixed(1)} out of 5`}>
              {renderStars()}
            </span>
            <span className="text-[#1A1A1A] font-semibold">{averageRating.toFixed(1)}</span>
            <span className="text-[#666666]">({reviewCount} reviews)</span>
          </div>

          <EnrollButton
            label="Enroll Now"
            variant="primary"
            size="md"
            context={FORM_CONTEXTS.COURSE_SIDEBAR}
            fullWidth={true}
            className="mb-3"
            onClick={handleEnroll}
          />

          <WhatsAppButton
            variant="outline"
            size="md"
            label="Ask Us on WhatsApp"
            message={whatsappMessages.course(courseData?.hero?.headline || courseTitle)}
            context="course_sidebar"
            className="w-full justify-center border-[#25D366] text-[#25D366] mb-3"
          />

          <EnrollButton
            label="Book Free Counseling"
            variant="ghost"
            size="sm"
            context={FORM_CONTEXTS.COUNSELING}
            icon={false}
            className="w-full text-center justify-center mb-3"
          />

          <div className="mt-5 rounded-lg border border-green-100 bg-green-50 px-3 py-2 inline-flex items-center gap-2">
            <ShieldCheck size={16} className="text-green-600" />
            <span className="text-xs font-semibold text-green-700">Satisfaction Guarantee</span>
          </div>
        </div>
      </aside>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white/95 backdrop-blur px-4 py-3">
        <div className="max-w-[1440px] mx-auto flex items-center gap-3">
          <EnrollButton
            label="Enroll Now"
            variant="primary"
            size="md"
            context={FORM_CONTEXTS.COURSE_MOBILE}
            icon={false}
            className="flex-1"
            onClick={handleEnroll}
          />
          <WhatsAppButton
            variant="icon-only"
            message={whatsappMessages.enroll(courseData?.hero?.headline || courseTitle)}
            context="course_mobile_bar"
            label="Chat on WhatsApp"
          />
        </div>
      </div>
    </>
  );
}

